import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup } from "@angular/forms";
import { IonInput, Platform } from "@ionic/angular";
import { Store } from "@ngrx/store";
import { TranslateService } from "@ngx-translate/core";
import { MqttConnectionState, MqttService } from "ngx-mqtt";
import { Subscription } from "rxjs";
import { filter, take, tap } from "rxjs/operators";
import { EnumStringConverter } from "src/components/shared-components/enum/enum-string-converter";
import { AppSettings } from "src/components/shared-components/class/general-classes/app-settings";
import { Plant } from "src/model/class/general classes/plant";
import { StaticClass } from "src/components/shared-components/class/general-classes/static-classes";
import { Settings } from "src/model/class/settings";
import { ApiErrorType } from "src/model/enum/enumApiErrorType";
import { BIGDeviceOperationMode } from "src/components/shared-components/enum/enumDevice";
import { LogLevel } from "src/components/shared-components/enum/enumLog";
import { ToastType } from "src/components/shared-components/enum/enumMisc";
import { ConnectionType } from "src/components/shared-components/enum/enumSettings";
import { IPVerificationStatus } from "src/model/enum/server-ip-verification";
import { iTranslation } from "src/components/shared-components/interfaces/iTranslation";
import { BIGService } from "src/service/big.service";
import { EventService } from "src/service/event.service";
import { LoggerService } from "src/service/logger.service";
import { MqttClientService } from "src/service/mqtt-client.service";
import { NetworkService } from "src/service/network.service";
import { SettingsService } from "src/service/settings.service";
import { ToastService } from "src/service/toast.service";
import { selectedPlant } from "src/state/selectors/plant.selectors";
import { serverSettings } from "src/state/selectors/server-settings-selector";
//
@Component({
    selector: "app-options-plant-specific",
    templateUrl: "./options-plant-specific.html",
    styleUrls: ["./options-plant-specific.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class OptionsPlantSpecificComponent implements OnInit, iTranslation {
  CurrentLanguage: string = "it";
  private _logContext: string = "Options-plant-specific.component";

  //#region Subcriptions to "_store.select(...)" Observables
  private _selectserverSettingsSub: Subscription = null;
  private _selectCurrentPlantSub: Subscription = null;

  //#endregion

  private _formSubscriptions: Array<Subscription> = null;
  private _networkServiceSubscription: Subscription = null;
  private _nativeMQTTServiceSub: Subscription = null;
  private _translateSubscription: Subscription = null;

  private _currentServerSettings: Settings = null;
  private _showStatusBar: boolean = false;
  public currentPlant: Plant = null;

  private _lblConnectionError: string =
    "Errore sconosciuto. Il server potrebbe non essere raggiungibile oppure contattare l'assistenza!";
  private _lblFaetureUnsupported: string = "Funzionalità non supportata";

  public formAppSetting: UntypedFormGroup = null;
  public currentConnectionType: ConnectionType = ConnectionType.NONE;
  public toolBarColor: string = "primary";

  public isTouchDevice: boolean = false;
  public operationModeString: string = "";
  public enumOperationeMode = BIGDeviceOperationMode;
  public currentOperationMode: BIGDeviceOperationMode =
    BIGDeviceOperationMode.STANDARD;

  public isAPIServerConnectionVerified: IPVerificationStatus =
    IPVerificationStatus.TEXT_RUNNING;
  public currentMqttConnectionStatus: MqttConnectionState;

  /**
   * MQTT connection state shown with the IP verification badge: closed = unreachable,
   * connecting = waiting for the check, connected = ok
   */
  public get mqttConnectionStatusForBadge(): IPVerificationStatus {
    switch (this.currentMqttConnectionStatus) {
      case MqttConnectionState.CONNECTED:
        return IPVerificationStatus.OK;
      case MqttConnectionState.CONNECTING:
        return IPVerificationStatus.WAIT_TO_CHECK;
      default:
        return IPVerificationStatus.UNREACHABLE;
    }
  }

  public MqttConnectionStateEnum = MqttConnectionState;

  public isNotBrowser: boolean = false;

  constructor(
    private _translateService: TranslateService,
    private _fb: UntypedFormBuilder,
    private _settingService: SettingsService,
    private _toastService: ToastService,
    private _networkService: NetworkService,
    private _store: Store,
    private _loggerService: LoggerService,
    private _mqttNativeService: MqttService,
    private _mqttClientService: MqttClientService,
    private _eventService: EventService,
    private _bigService: BIGService,
    private _platform: Platform
  ) { }

  /**
   * Subscribe to the public Observable of "NetworkService", which emits each time the connection change of type/state. A local property is changed to
   * reflect the new value.
   */
  private subscribeNetworkServiceData(): void {
    this._networkServiceSubscription =
      this._networkService.currentConnectionType.subscribe(
        (currentConnectionType: ConnectionType) => {
          this.currentConnectionType = currentConnectionType;

          this.toolBarColor = StaticClass.getComponentColorByNetworkState(
            this.currentConnectionType
          );

          if (
            this.currentPlant?.operativeMode === BIGDeviceOperationMode.STANDARD
          ) {
            this.isAPIServerConnectionVerified =
              this.convertConnectionTypetoIPConnection(currentConnectionType);
          }
        }
      );
  }

  ngOnInit() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      "ngOnInit()",
      "START"
    );

    this.checkIfDeviceIsMyTO();
    this.checkMobileCondition();
    this.subscribeToMqttNativeService();

    this.initializeForm();
    this.subscribeFormEvent();
    this.subscribeUserSettings();
    this.subscribeCurrentPlant();

    this.loadTranslation();
    this.subscribeTranslationEvent();
    this.subscribeNetworkServiceData();
  }

  ngOnDestroy() {
    this._loggerService.log(
      LogLevel.LOG_INFO,
      this._logContext,
      this._logContext,
      " ngOnDestroy() ",
      "DESTROY"
    );

    this.unsubscribeFormEvent();

    this.unsubscribeStoreSelect();

    if (this._translateSubscription) {
      this._translateSubscription.unsubscribe();
    }
    if (this._nativeMQTTServiceSub) {
      this._nativeMQTTServiceSub.unsubscribe();
    }
    this._networkServiceSubscription.unsubscribe();
  }

  //#region  Translation function
  /**
   * Handle native translation event
   */
  public subscribeTranslationEvent(): void {
    this._translateSubscription = this._translateService.onLangChange.subscribe(
      () => {
        this.loadTranslation();
      }
    );
  }

  /**
   * Load translated strings
   */
  public loadTranslation(): void {
    this._translateService
      .get(["dictionary.loginFailed", "dictionary.functionalityNotSupported"])
      .subscribe((translatedStrings) => {
        this._lblConnectionError = translatedStrings["dictionary.loginFailed"];
        this._lblFaetureUnsupported =
          translatedStrings["dictionary.functionalityNotSupported"];

        this.operationModeString =
          EnumStringConverter.operationModeEnumAsString(
            this.currentPlant?.operativeMode,
            this._translateService
          );
      });
  }
  //#endregion

  /**
   * Perform verifying Connection to API Server; since this happen ONLY if the app is running in "CONTROL_ACCESS_MODE", the IP address used is directly taken
   * from "FullLanAddress" propery of plant.
   */
  public async onVerifyAPIServerConnection(): Promise<void> {
    this.isAPIServerConnectionVerified = IPVerificationStatus.TEXT_RUNNING;

    try {
      const fullAddress: string = this.currentPlant.FullLanAddress;

      this._loggerService.log(
        LogLevel.LOG_INFO,
        this._logContext,
        this._logContext,
        "onVerifyAPIServerConnection()",
        "Testing FullLanAddress: " + fullAddress
      );

      const resultConnection: boolean =
        await this._networkService.sendServerHeartbit(
          //this.currentPlant.FullLanAddress,
          fullAddress,
          AppSettings.DEFAULT_LAN_TIMEOUT_MS
        );

      if (resultConnection) {
        this.isAPIServerConnectionVerified = IPVerificationStatus.OK;
      } else throw new Error();
    } catch (exception) {
      this._loggerService.log(
        LogLevel.LOG_ERROR,
        this._logContext,
        this._logContext,
        "onVerifyAPIServerConnection()",
        "ERROR: " + exception.message
      );
      this.isAPIServerConnectionVerified = IPVerificationStatus.UNREACHABLE;
    }
  }

  /**
   * This method handle reaction to interaction with connect/disconnect from MQTT broker button, based on current status of connection to the server.
   */
  public async onConnectDisconnectMQTT(
    currentMQTTConnectionStatus: MqttConnectionState
  ): Promise<void> {
    switch (currentMQTTConnectionStatus) {
      case MqttConnectionState.CLOSED:
        await this._mqttClientService.connect();
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "onConnectDisconnectMQTT()",
          "Connection succesful!"
        );
        break;
      case MqttConnectionState.CONNECTED:
        this._eventService.OnRequestDisconnectMqtt.next(null);
        break;

      default:
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          this._logContext,
          "onConnectDisconnectMQTT()",
          "Connection attempt underway..."
        );
        break;
    }
  }

  /** GF: used to keep the current status */
  private subscribeToMqttNativeService(): void {
    this._nativeMQTTServiceSub = this._mqttNativeService.state.subscribe(
      (currentStatus: MqttConnectionState) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "mqttService",
          "state",
          "Status mqtt state connection from service: " + currentStatus
        );

        this.currentMqttConnectionStatus = currentStatus;
      }
    );
  }

  /**
   * Initialize form settings field with default value
   */
  private initializeForm(): void {
    this.formAppSetting = this._fb.group({
      viewStatusBar: new UntypedFormControl(false),
    });
  }

  private subscribeUserSettings(): void {
    this._selectserverSettingsSub = this._store
      .select(serverSettings)
      .pipe(
        filter((serverSettings) => serverSettings !== null),
        /* Only the first (not-null) emission is considered, since the "client-settings" are in this page ONLY READ when entering the page */
        take(1),
        tap((serverSettings: Settings) => {
          this._currentServerSettings = serverSettings;
        })
      )
      .subscribe((serverSettings: Settings) => {
        this._showStatusBar = this._currentServerSettings.ShowStatusBar;

        this.updateFormField();
      });
  }

  private subscribeCurrentPlant(): void {
    this._selectCurrentPlantSub = this._store
      .select(selectedPlant)
      .pipe(
        filter((currentPlant) => currentPlant !== null)

        /* Only the first (not-null) emission is considered, since the "client-settings" are in this page ONLY READ when entering the page */
        /*
      take(1),
*/
      )
      .subscribe(async (currentPlant: Plant) => {
        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "._store.select(selectedPlant)",
          " subscribeCurrentPlant() ",
          "FROM STORE"
        );

        this.currentPlant = new Plant(currentPlant);

        this.operationModeString =
          EnumStringConverter.operationModeEnumAsString(
            currentPlant.operativeMode,
            this._translateService
          );
        this.currentOperationMode = currentPlant.operativeMode;

        this._loggerService.log(
          LogLevel.LOG_INFO,
          this._logContext,
          "._store.select(selectedPlant)",
          " subscribeCurrentPlant() ",
          "currentPlant.operativeMode: " +
          currentPlant.operativeMode +
          " || currentOperationMode: " +
          this.currentOperationMode
          /*+
          " | VALUE: " 
          +
          BIGDeviceOperationMode.ACCESS_CONTROL_MODE
          */
        );
      });
  }

  /**
   * Check if current device is MyTO device
   */
  private checkIfDeviceIsMyTO(): void {
    this.isTouchDevice = this._bigService.isMyToDevice;

    this.isTouchDevice = true;
  }

  //#region Unsubscribe from "_store.selec(...)" Observable

  /**
   * This method handles the unsunìbscription from the Observables returned by the "_store.select(...)" invocations.
   * It SHOULD BE CALLED EACH TIME THE DATA STREAMS ARE NOT NEEDED ANYMORE; this means in:
   *
   * * OnDestroy() [Components/Services]
   * * OnViewWillLeave() [Pages]
   */
  private unsubscribeStoreSelect(): void {
    if (this._selectserverSettingsSub) {
      this._selectserverSettingsSub.unsubscribe();
    }

    if (this._selectCurrentPlantSub) {
      this._selectCurrentPlantSub.unsubscribe();
    }
  }

  //#endregion

  /**
   * Update form field with settings storage values
   */
  private updateFormField(): void {
    this.formAppSetting.patchValue(
      {
        viewStatusBar: StaticClass.booleanize(this._showStatusBar),
      },
      { emitEvent: false }
    );
  }

  /**
   * Subscribe value change event from each form field.
   * There are different subscription because each form field handle specific
   * event service propagation
   */
  private subscribeFormEvent() {
    this._formSubscriptions = new Array<Subscription>();

    this._formSubscriptions.push(
      this.formAppSetting
        .get("viewStatusBar")
        .valueChanges.subscribe(async (formUpdatedField: boolean) => {
          const resultUpdate = await this.performUpdateSetting(
            "ShowStatusBar",
            formUpdatedField.toString().toUpperCase()
          );

          if (resultUpdate) {
            this._showStatusBar = formUpdatedField;
          } else {
            /* If the API call fails, the toggle-elements are resetted (through the form-controls) to _currentUserSettings's values*/
            this.updateFormField();
          }
        })
    );
  }

  private unsubscribeFormEvent() {
    for (const sub of this._formSubscriptions) {
      sub.unsubscribe();
    }
  }

  /**
   * Centralises update to "general settings" which are collected inside the "setting.ts" model as properties.
   *
   * @param settingName Name of the updated setting; it must be included both in the local model "settings" (as a property) and in the server-side
   * Schema and DB-table (as a column-name).
   *
   * @param settingValue New value the specified setting shall be updated with; MUST be casted as STRING
   *
   * @returns TRUE if the update is terminate succesfully, FALSE otherwise.
   */
  private async performUpdateSetting(
    settingName: string,
    settingValue: string
  ): Promise<boolean> {
    let errorMessage: string = this._lblConnectionError;

    try {
      await this._settingService.updateUserSetting(settingName, settingValue);
      return true;
    } catch (exception) {
      //Spostato in servizio.
      /* If the API  error denotes a not-implemented-feature, change message in error toast */

      if (exception.status === ApiErrorType.NOT_FOUND) {
        errorMessage = this._lblFaetureUnsupported;
      }

      this._toastService.showToast(ToastType.ERROR, errorMessage, 3000);

      return false;
    }
  }

  private convertConnectionTypetoIPConnection(
    connectionType: ConnectionType
  ): IPVerificationStatus {
    let ipVerification = IPVerificationStatus.UNREACHABLE;
    switch (connectionType) {
      case ConnectionType.NONE:
        ipVerification = IPVerificationStatus.UNREACHABLE;
        break;
      case ConnectionType.LAN:
      case ConnectionType.WAN:
      case ConnectionType.LANWAN:
        ipVerification = IPVerificationStatus.OK;
        break;
    }

    return ipVerification;
  }

  /**
   * Check mobile condition.
   */
  private checkMobileCondition(): void {
    this.isNotBrowser =
      this._platform.is("ios") || this._platform.is("android");
  }

}
